// DeckCatalogPage — Zweitfenster-Route des Karten-Katalogs (`/decks/:deckId/katalog`):
// dieselbe `CardCatalog`-Komponente wie die Seitenspalte des Editors, hier als eigenständige
// Seite für einen zweiten Monitor. Liest denselben Query-Key + dieselbe URL wie
// `DeckEditorRoute` — beide Fenster teilen sich so dieselbe Deck-Antwort, sobald der
// Fenster-Abgleich (`useDeckSyncChannel`) oder der Fokus-Refetch
// (`useRefetchOnWindowFocus`) sie synchronisiert.
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderX, RefreshCw, SearchX } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { json } from '../api/apiClient';
import Page from '../ui/Page';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import ActionBar from '../ui/ActionBar';
import { useToast } from '../ui/Toast';
import CardCatalog from '../features/deckEditor/catalog/CardCatalog';
import useDeckCardActions from '../features/deckEditor/hooks/useDeckCardActions';
import { useRefetchOnWindowFocus } from '../features/deckEditor/hooks/useRefetchOnWindowFocus';
import { useDeckSyncChannel } from '../features/deckEditor/hooks/useDeckSyncChannel';
import { commanderColorFilter } from '../features/deckEditor/utils/commanderColorFilter';
import { deckNumericId } from '../features/deckEditor/utils/deckIds';
import './DeckCatalogPage.css';

export default function DeckCatalogPage() {
  const { deckId } = useParams();
  const { authToken, currentUser, isAdmin } = useAuth();
  const { viewedUserId, isOwnProfile } = useProfile();
  const queryClient = useQueryClient();
  const { push: pushToast } = useToast();
  const [activeTab, setActiveTab] = useState('suggestions');

  // Identischer Key + identische URL wie DeckEditorRoute: nur so treffen beide Fenster
  // denselben Cache-Eintrag (im eigenen Prozess je Fenster, aber serverseitig dieselbe Antwort).
  const queryKey = useMemo(() => ['deck-editor', deckId, viewedUserId], [deckId, viewedUserId]);

  const deckQuery = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const viewerParam = currentUser?.id ? `viewerUserId=${encodeURIComponent(currentUser.id)}` : '';
      const ownerParam = viewedUserId ? `userId=${encodeURIComponent(viewedUserId)}` : '';
      const editorParam = 'editor=true';
      const qs = [ownerParam, viewerParam, editorParam].filter(Boolean).join('&');
      const url = qs ? `/api/decks/${deckId}?${qs}` : `/api/decks/${deckId}`;
      return await json(url, { signal });
    },
    enabled: !!deckId && !!authToken,
    staleTime: 1000 * 60 * 5,
  });

  const handleDeckUpdate = useCallback((update) => {
    queryClient.setQueryData(queryKey, (prev) => (typeof update === 'function' ? update(prev) : update));
  }, [queryClient, queryKey]);

  const deck = deckQuery.data;
  const numericDeckId = useMemo(() => deckNumericId(deck), [deck]);
  // Startwert der Suche: die Farben des Commanders (Katalog-Vertrag `initialColorIdentity`).
  const colorIdentity = useMemo(() => commanderColorFilter(deck?.cards), [deck]);
  useRefetchOnWindowFocus(numericDeckId);
  useDeckSyncChannel({ uiDeckId: deckId, numericDeckId });

  // Deckt sich mit DeckEditorPage.canEditDeck: nur eigene, virtuelle Decks bekommen
  // einen Katalog — physische Decks haben keine katalogfähigen Karten, fremde keine Rechte.
  const canEditDeck = Boolean(isOwnProfile && deck && deck.source !== 'physical');

  const handleAddCardError = useCallback((message) => {
    pushToast({ tone: 'error', message });
  }, [pushToast]);

  const { addCardByScryfallId } = useDeckCardActions({
    deck,
    canEdit: canEditDeck,
    onDeckUpdate: handleDeckUpdate,
    onError: handleAddCardError,
  });

  // Erfolg/Dublette meldet der Katalog selbst (sein Vertrag) — hier bleibt nur die Abbildung
  // auf die Mutation, kein zusätzlicher Toast (sonst zeigte ein Add zwei Meldungen).
  const handleAddCard = useCallback((scryfallId, { categoryId } = {}) => (
    addCardByScryfallId(scryfallId, { categoryId })
  ), [addCardByScryfallId]);

  if (deckQuery.isLoading) {
    return (
      <Page width="full">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} aria-busy="true">
          <Skeleton width="320px" height="34px" />
          <Skeleton height="60vh" radius="lg" />
        </div>
      </Page>
    );
  }

  if (deckQuery.error?.status === 404) {
    return (
      <Page width="full">
        <EmptyState
          icon={SearchX}
          title="Deck nicht mehr vorhanden"
          description="Dieses Deck existiert nicht mehr oder wurde gelöscht."
          action={<Link to="/decks" className="btn-secondary">Zur Deck-Übersicht</Link>}
        />
      </Page>
    );
  }

  if (deckQuery.error) {
    return (
      <Page width="full">
        <EmptyState
          variant="error"
          icon={RefreshCw}
          title="Deck konnte nicht geladen werden"
          description="Prüfe deine Verbindung und versuche es erneut."
          onRetry={() => deckQuery.refetch()}
        />
      </Page>
    );
  }

  if (!deck) {
    return (
      <Page width="full">
        <EmptyState icon={SearchX} title="Deck nicht gefunden" description="Dieses Deck existiert nicht oder wurde gelöscht." />
      </Page>
    );
  }

  if (!canEditDeck) {
    return (
      <Page width="full">
        <EmptyState
          icon={FolderX}
          title="Katalog nur für eigene virtuelle Decks"
          description="Physische Decks und Decks anderer Profile haben hier keinen Katalog."
          action={<Link to="/decks" className="btn-secondary">Zur Deck-Übersicht</Link>}
        />
      </Page>
    );
  }

  return (
    <Page width="full" className="deck-catalog-page">
      <ActionBar left={<ActionBar.Title>Katalog — {deck.name}</ActionBar.Title>} divider />
      <div className="deck-catalog-page__body">
        <CardCatalog
          deckId={numericDeckId}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onAddCard={handleAddCard}
          isAdmin={isAdmin}
          dndEnabled={false}
          initialColorIdentity={colorIdentity}
        />
      </div>
    </Page>
  );
}
